/**
 * Copyright (C) 2026 Checkmk GmbH - License: Checkmk Enterprise License
 * This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
 * conditions defined in the file COPYING, which is part of this source code package.
 */
import type { components, paths } from 'cmk-ai-control-plane/typescript/openapi'

import type { FastApiEvent } from '@/lib/fastapi-client/client'

import type { ControlPlaneClient } from '@/nonfree/ai/control-plane/api/transport'

type Schemas = components['schemas']

export type ConversationSummary = Schemas['ConversationSummary']
export type ConversationResponse = Schemas['ConversationResponse']
type StreamBlock = FastApiEvent<paths, '/conversations/{conversation_id}/events'>['data']
// An end event only says that no turn runs, it is no block of the conversation
export type ContentBlock = Exclude<StreamBlock, { type: 'end' }>

export interface ConversationEvent {
  cursor: number
  block: StreamBlock
}

export class ConversationsApi {
  public constructor(private readonly client: ControlPlaneClient) {}

  public async create(
    idempotencyKey: string,
    signal: AbortSignal
  ): Promise<Schemas['ConversationCreatedResponse']> {
    return this.client.POST('/conversations', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      signal
    })
  }

  public async list(): Promise<ConversationSummary[]> {
    return (await this.client.GET('/conversations')).conversations
  }

  public async get(conversationId: string): Promise<ConversationResponse> {
    return this.client.GET('/conversations/{conversation_id}', {
      params: { path: { conversation_id: conversationId } }
    })
  }

  public async delete(conversationId: string): Promise<void> {
    await this.client.DELETE('/conversations/{conversation_id}', {
      params: { path: { conversation_id: conversationId } }
    })
  }

  public async postTurn(
    conversationId: string,
    prompt: string,
    idempotencyKey: string,
    signal: AbortSignal
  ): Promise<Schemas['TurnResponse']> {
    return this.client.POST('/conversations/{conversation_id}/turns', {
      params: {
        path: { conversation_id: conversationId },
        header: { 'Idempotency-Key': idempotencyKey }
      },
      body: { prompt },
      signal
    })
  }

  public async cancel(conversationId: string): Promise<void> {
    await this.client.POST('/conversations/{conversation_id}/cancel', {
      params: { path: { conversation_id: conversationId } }
    })
  }

  /**
   * The events of the running turn after the one whose cursor is `after`; 0 reads the turn from
   * its prompt. While no turn runs, the only event is an end event, which has no cursor.
   */
  public async *events(
    conversationId: string,
    after: number,
    signal: AbortSignal
  ): AsyncIterable<ConversationEvent> {
    const events = this.client.events('/conversations/{conversation_id}/events', {
      params: { path: { conversation_id: conversationId }, header: { 'Last-Event-ID': after } },
      signal
    })
    for await (const { id, data } of events) {
      yield { cursor: Number(id), block: data }
    }
  }
}
